import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { loadSessionField } from '../../../auth/sessionProfileStore'
import { ACTION_FEEDBACK_DURATION_MS } from '../../../components/alerts/ActionFeedbackAlert'
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog'
import LoadingScreen from '../../../components/LoadingScreen'
import { isAuthRedirectError } from '../../../auth'
import { MUI_TABLE_PAGINATION_OPTIONS } from '../../../components/MuiTablePagination'
import RbacAddUsersToRoleDialog from '../../components/rbac-add-users-to-role-dialog/RbacAddUsersToRoleDialog'
import RbacReassignUserDialog from '../../components/RbacReassignUserDialog'
import RbacRoleConfirmDialog from '../components/RbacRoleConfirmDialog'
import RbacRoleDetailContent from '../components/RbacRoleDetailContent'
import RbacUserDetailHeader from '../../components/RbacUserDetailHeader'
import {
  canManageRbacRoles,
  canManageRbacUsers
} from '../../../global_context/capabilities/rbacManagementCapabilities'
import { assignUserRoles, fetchCurrentUser } from '../../services/rbacApi'
import { getRbacIsAdmin, refreshRbacFromCurrentUser } from '../../../auth/sessionRbac'
import { fetchPermissionCatalog } from '../services/permissionCatalogApi'
import { fetchRole, fetchRoleHistory, fetchRoleUsers, fetchRoles, updateRole } from '../services/rolesApi'
import {
  RBAC_ROLE_DETAIL_TAB_VALUE,
  RBAC_ROLE_DETAIL_TEXTS,
  RBAC_ROLE_HISTORY_TAB_VALUE,
  RBAC_ROLE_PERMISSIONS_TAB_VALUE,
  RBAC_ROLE_USERS_TAB_VALUE
} from '../shared/rbacRoleDetailConstants'
import {
  getRbacRoleCannotInactivateMessage,
  getRbacRoleStatusMeta,
  roleHasAssignedUsers
} from '../shared/rbacRoleDetailHelpers'
import {
  UI_COLOR_DANGER_ACTION,
  UI_COLOR_DANGER_ACTION_HOVER,
  UI_COLOR_PRIMARY,
  UI_COLOR_PRIMARY_HOVER
} from '../../../constants/uiColors'

const DEFAULT_LIST_PER_PAGE = 10
const ROLES_INDEX_PATH = '/configuracion/rbac/roles'
const RBAC_ROLE_DETAIL_TAB_VALUES = new Set([
  RBAC_ROLE_DETAIL_TAB_VALUE,
  RBAC_ROLE_PERMISSIONS_TAB_VALUE,
  RBAC_ROLE_USERS_TAB_VALUE,
  RBAC_ROLE_HISTORY_TAB_VALUE
])
const RBAC_ROLE_DETAIL_TAB_QUERY_KEY = 'tab'

function resolveRoleDetailTab(value) {
  return RBAC_ROLE_DETAIL_TAB_VALUES.has(value) ? value : RBAC_ROLE_DETAIL_TAB_VALUE
}

export default function RbacRoleDetailPage() {
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const canManage = canManageRbacRoles()
  const canManageUsers = canManageRbacUsers()
  const isAdmin = getRbacIsAdmin()
  const requestedTab = resolveRoleDetailTab(searchParams.get(RBAC_ROLE_DETAIL_TAB_QUERY_KEY))

  const [role, setRole] = useState(null)
  const [catalog, setCatalog] = useState(null)
  const [roles, setRoles] = useState([])
  const [activeTab, setActiveTab] = useState(requestedTab)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [actionAlert, setActionAlert] = useState(null)
  const [statusOpen, setStatusOpen] = useState(false)
  const [cannotInactivateOpen, setCannotInactivateOpen] = useState(false)
  const [savingStatus, setSavingStatus] = useState(false)
  const [userActionMenuAnchorEl, setUserActionMenuAnchorEl] = useState(null)
  const [userActionMenu, setUserActionMenu] = useState(null)
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false)
  const [reassignTarget, setReassignTarget] = useState(null)
  const [savingReassign, setSavingReassign] = useState(false)
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false)

  const [usersRows, setUsersRows] = useState([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [usersLoaded, setUsersLoaded] = useState(false)
  const [usersPagination, setUsersPagination] = useState({
    current_page: 1,
    per_page: DEFAULT_LIST_PER_PAGE,
    total_count: 0,
    total_pages: 1
  })

  const [historyRows, setHistoryRows] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyLoaded, setHistoryLoaded] = useState(false)
  const [historyPagination, setHistoryPagination] = useState({
    current_page: 1,
    per_page: DEFAULT_LIST_PER_PAGE,
    total_count: 0,
    total_pages: 1
  })

  const loadRole = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      setUsersLoaded(false)
      setUsersRows([])
      setHistoryLoaded(false)
      setHistoryRows([])
      const [roleData, catalogData, rolesData] = await Promise.all([
        fetchRole(id),
        fetchPermissionCatalog(),
        fetchRoles()
      ])
      setRole(roleData)
      setCatalog(catalogData)
      setRoles(Array.isArray(rolesData) ? rolesData : [])
      setLoading(false)
    } catch (err) {
      if (isAuthRedirectError(err)) return
      setError(err.message)
      setLoading(false)
    }
  }, [id])

  const loadUsers = useCallback(async () => {
    try {
      setUsersLoading(true)
      const data = await fetchRoleUsers(id, {
        page: usersPagination.current_page,
        per_page: usersPagination.per_page
      })
      const totalCount = data.pagination?.total || 0
      setUsersRows(data.users || [])
      setUsersPagination((prev) => ({
        ...prev,
        total_count: totalCount,
        total_pages: Math.max(Math.ceil(totalCount / prev.per_page), 1)
      }))
    } catch (err) {
      if (isAuthRedirectError(err)) return
      setError(err.message)
    } finally {
      setUsersLoaded(true)
      setUsersLoading(false)
    }
  }, [id, usersPagination.current_page, usersPagination.per_page])

  const loadHistory = useCallback(async () => {
    try {
      setHistoryLoading(true)
      const data = await fetchRoleHistory(id, {
        page: historyPagination.current_page,
        per_page: historyPagination.per_page
      })
      const totalCount = data.pagination?.total || 0
      setHistoryRows(data.results || [])
      setHistoryPagination((prev) => ({
        ...prev,
        total_count: totalCount,
        total_pages: Math.max(Math.ceil(totalCount / prev.per_page), 1)
      }))
    } catch (err) {
      if (isAuthRedirectError(err)) return
      setError(err.message)
    } finally {
      setHistoryLoaded(true)
      setHistoryLoading(false)
    }
  }, [historyPagination.current_page, historyPagination.per_page, id])

  useEffect(() => {
    loadRole()
  }, [loadRole])

  useEffect(() => {
    setActiveTab((prevTab) => (prevTab === requestedTab ? prevTab : requestedTab))
  }, [requestedTab])

  useEffect(() => {
    if (activeTab === RBAC_ROLE_USERS_TAB_VALUE) loadUsers()
  }, [activeTab, loadUsers])

  useEffect(() => {
    if (activeTab === RBAC_ROLE_HISTORY_TAB_VALUE) loadHistory()
  }, [activeTab, loadHistory])

  useEffect(() => {
    if (!actionAlert) return undefined

    const dismissTimer = window.setTimeout(() => {
      setActionAlert(null)
    }, ACTION_FEEDBACK_DURATION_MS)

    return () => window.clearTimeout(dismissTimer)
  }, [actionAlert])

  const backToRoles = () => {
    navigate(location.state?.from || ROLES_INDEX_PATH)
  }

  const showActionAlert = (message, severity = 'success') => {
    setActionAlert({
      id: Date.now(),
      severity,
      message
    })
  }

  const handleEditRole = () => {
    navigate(`/configuracion/rbac/roles/${id}/editar`)
  }

  const handleStatusAction = () => {
    if (getRbacRoleStatusMeta(role).isActive && roleHasAssignedUsers(role)) {
      setCannotInactivateOpen(true)
      return
    }

    setStatusOpen(true)
  }

  const openUserActionMenu = (event, user) => {
    event.stopPropagation()
    setUserActionMenuAnchorEl(event.currentTarget)
    setUserActionMenu(user)
  }

  const closeUserActionMenu = () => {
    setUserActionMenuAnchorEl(null)
  }

  const handleUserActionMenuExited = () => {
    setUserActionMenu(null)
  }

  const goToUserDetail = (user) => {
    if (!user?.id) return

    navigate(`/configuracion/rbac/usuarios/${user.id}`, {
      state: {
        from: `${location.pathname}${location.search}`
      }
    })
  }

  const handleUserRowClick = (userId) => {
    goToUserDetail({ id: userId })
  }

  const openReassignDialog = (user) => {
    setReassignTarget(user)
    setReassignDialogOpen(true)
  }

  const closeReassignDialog = () => {
    setReassignDialogOpen(false)
  }

  const handleReassignDialogExited = () => {
    setReassignTarget(null)
  }

  const handleReassignUserAction = () => {
    const user = userActionMenu
    closeUserActionMenu()
    if (user) openReassignDialog(user)
  }

  const handleViewUserAction = () => {
    const user = userActionMenu
    closeUserActionMenu()
    if (user) goToUserDetail(user)
  }

  const handleReassignUser = async (roleId) => {
    if (!reassignTarget || !roleId) return

    try {
      setSavingReassign(true)
      setError('')
      await assignUserRoles(reassignTarget.id, [roleId])

      const currentUserId = loadSessionField('id')
      if (String(currentUserId) === String(reassignTarget.id)) {
        await refreshRbacFromCurrentUser(fetchCurrentUser)
      }

      await loadUsers()
      showActionAlert(
        `Usuario ${reassignTarget.name || reassignTarget.email || ''} reasignado correctamente.`
      )
      closeReassignDialog()
    } catch (err) {
      if (isAuthRedirectError(err)) return
      setError(err.message)
    } finally {
      setSavingReassign(false)
    }
  }

  const openAddUsersDialog = () => {
    setBulkDialogOpen(true)
  }

  const closeAddUsersDialog = () => {
    setBulkDialogOpen(false)
  }

  const handleAddUsersSuccess = async (userIds = []) => {
    const currentUserId = loadSessionField('id')
    if (userIds.some((userId) => String(userId) === String(currentUserId))) {
      try {
        await refreshRbacFromCurrentUser(fetchCurrentUser)
      } catch (_err) {
        // The current user session may already have been revoked.
      }
    }

    await loadUsers()
    showActionAlert('Los usuarios se agregaron correctamente al rol.')
  }

  const handleTabChange = (nextTab) => {
    const nextActiveTab = resolveRoleDetailTab(nextTab)
    const nextSearchParams = new URLSearchParams(searchParams)

    setActiveTab(nextActiveTab)

    if (nextActiveTab === RBAC_ROLE_DETAIL_TAB_VALUE) {
      nextSearchParams.delete(RBAC_ROLE_DETAIL_TAB_QUERY_KEY)
    } else {
      nextSearchParams.set(RBAC_ROLE_DETAIL_TAB_QUERY_KEY, nextActiveTab)
    }

    if (nextSearchParams.toString() !== searchParams.toString()) {
      setSearchParams(nextSearchParams, { replace: true })
    }
  }

  const closeCannotInactivateDialog = () => {
    setCannotInactivateOpen(false)
  }

  const handleGoToReassignUsers = () => {
    closeCannotInactivateDialog()
    handleTabChange(RBAC_ROLE_USERS_TAB_VALUE)
  }

  const handleUpdateStatus = async () => {
    if (!role) return

    if (getRbacRoleStatusMeta(role).isActive && roleHasAssignedUsers(role)) {
      setStatusOpen(false)
      setCannotInactivateOpen(true)
      return
    }

    try {
      setSavingStatus(true)
      setError('')
      const statusMeta = getRbacRoleStatusMeta(role)
      const willActivate = !statusMeta.isActive
      const updated = await updateRole(id, {
        status: willActivate ? 'active' : 'inactive'
      })
      setRole(updated)
      showActionAlert(
        willActivate
          ? `Rol ${updated.name || role.name} activado correctamente.`
          : `Rol ${updated.name || role.name} inactivado correctamente.`
      )
      if (activeTab === RBAC_ROLE_HISTORY_TAB_VALUE) {
        loadHistory()
      }
      setStatusOpen(false)
      setSavingStatus(false)
    } catch (err) {
      if (isAuthRedirectError(err)) return
      setError(err.message)
      setSavingStatus(false)
    }
  }

  const handleUsersPageChange = (_event, nextPage) => {
    setUsersLoading(true)
    setUsersPagination((prev) => ({ ...prev, current_page: nextPage }))
  }

  const handleUsersRowsPerPageChange = (_event, nextRowsPerPage) => {
    const safeRowsPerPage = MUI_TABLE_PAGINATION_OPTIONS.includes(nextRowsPerPage)
      ? nextRowsPerPage
      : DEFAULT_LIST_PER_PAGE
    setUsersLoading(true)
    setUsersPagination((prev) => ({
      ...prev,
      per_page: safeRowsPerPage,
      current_page: 1
    }))
  }

  const handleHistoryPageChange = (_event, nextPage) => {
    setHistoryLoading(true)
    setHistoryPagination((prev) => ({ ...prev, current_page: nextPage }))
  }

  const handleHistoryRowsPerPageChange = (_event, nextRowsPerPage) => {
    const safeRowsPerPage = MUI_TABLE_PAGINATION_OPTIONS.includes(nextRowsPerPage)
      ? nextRowsPerPage
      : DEFAULT_LIST_PER_PAGE
    setHistoryLoading(true)
    setHistoryPagination((prev) => ({
      ...prev,
      per_page: safeRowsPerPage,
      current_page: 1
    }))
  }

  if (loading) {
    return <LoadingScreen text={RBAC_ROLE_DETAIL_TEXTS.loading} />
  }

  const statusMeta = getRbacRoleStatusMeta(role)
  const headerTitle = role ? `Rol: ${role.uid || role.id} - ${role.name || ''}` : 'Detalle del rol'
  const currentRoleOption = roles.find((option) => String(option.id) === String(role?.id)) || role
  const shouldShowUsersLoading =
    usersLoading || (activeTab === RBAC_ROLE_USERS_TAB_VALUE && !usersLoaded)
  const shouldShowHistoryLoading =
    historyLoading || (activeTab === RBAC_ROLE_HISTORY_TAB_VALUE && !historyLoaded)

  return (
    <>
      <RbacUserDetailHeader
        title={headerTitle}
        backLabel={RBAC_ROLE_DETAIL_TEXTS.backToRolesLabel}
        actionAlert={actionAlert}
        onBack={backToRoles}
        onCloseAlert={() => setActionAlert(null)}
      />

      <RbacRoleDetailContent
        error={error}
        role={role}
        catalog={catalog}
        canManage={canManage}
        canManageUsers={canManageUsers}
        isAdmin={isAdmin}
        activeTab={activeTab}
        usersRows={usersRows}
        usersLoading={shouldShowUsersLoading}
        usersPagination={usersPagination}
        userActionMenuAnchorEl={userActionMenuAnchorEl}
        userActionMenu={userActionMenu}
        historyRows={historyRows}
        historyLoading={shouldShowHistoryLoading}
        historyPagination={historyPagination}
        onDismissError={() => setError('')}
        onBack={backToRoles}
        onTabChange={handleTabChange}
        onEditRole={handleEditRole}
        onStatusAction={handleStatusAction}
        onAddUsers={openAddUsersDialog}
        onUserRowClick={handleUserRowClick}
        onOpenUserActionMenu={openUserActionMenu}
        onCloseUserActionMenu={closeUserActionMenu}
        onUserActionMenuExited={handleUserActionMenuExited}
        onReassignUserAction={handleReassignUserAction}
        onViewUserAction={handleViewUserAction}
        onUsersPageChange={handleUsersPageChange}
        onUsersRowsPerPageChange={handleUsersRowsPerPageChange}
        onHistoryPageChange={handleHistoryPageChange}
        onHistoryRowsPerPageChange={handleHistoryRowsPerPageChange}
      />

      <ConfirmDialog
        open={cannotInactivateOpen}
        title={RBAC_ROLE_DETAIL_TEXTS.cannotInactivateModalTitle}
        message={getRbacRoleCannotInactivateMessage(role)}
        cancelLabel={RBAC_ROLE_DETAIL_TEXTS.cancel}
        confirmLabel={RBAC_ROLE_DETAIL_TEXTS.cannotInactivateModalConfirm}
        onClose={closeCannotInactivateDialog}
        onConfirm={handleGoToReassignUsers}
        titleId="rbac-role-cannot-inactivate-title"
      />

      <RbacRoleConfirmDialog
        open={statusOpen}
        onClose={() => setStatusOpen(false)}
        onConfirm={handleUpdateStatus}
        role={role}
        title={
          statusMeta.isActive
            ? RBAC_ROLE_DETAIL_TEXTS.deactivateModalTitle
            : RBAC_ROLE_DETAIL_TEXTS.activateModalTitle
        }
        body={
          statusMeta.isActive
            ? RBAC_ROLE_DETAIL_TEXTS.deactivateModalBody
            : RBAC_ROLE_DETAIL_TEXTS.activateModalBody
        }
        confirmLabel={
          statusMeta.isActive
            ? RBAC_ROLE_DETAIL_TEXTS.inactivateRole
            : RBAC_ROLE_DETAIL_TEXTS.activateRole
        }
        confirmColor={statusMeta.isActive ? UI_COLOR_DANGER_ACTION : UI_COLOR_PRIMARY}
        confirmHoverColor={
          statusMeta.isActive ? UI_COLOR_DANGER_ACTION_HOVER : UI_COLOR_PRIMARY_HOVER
        }
        submitting={savingStatus}
      />

      <RbacReassignUserDialog
        open={reassignDialogOpen}
        onClose={closeReassignDialog}
        onExited={handleReassignDialogExited}
        onSubmit={handleReassignUser}
        user={reassignTarget}
        roles={roles}
        submitting={savingReassign}
      />

      <RbacAddUsersToRoleDialog
        open={bulkDialogOpen}
        onClose={closeAddUsersDialog}
        onSuccess={handleAddUsersSuccess}
        role={currentRoleOption}
      />
    </>
  )
}
